import * as moment from 'moment';
import { Entity, Type } from '@decahedron/entity';
import Variables from '@/config/variables';
import Picture from '@/entities/Picture';
import CarOption from '@/entities/CarOption';
import CarDamage from '@/entities/CarDamage';
import Formatter from '@/support/formatter';

export default class Car extends Entity {
    private variables = new Variables();

    protected formatter = new Formatter();

    public id: string = null;

    public make: string = null;

    public model: string = null;

    public title: string = null;

    public description: string = null;

    public firstRegistration: string = null;

    public mileage: number = null;

    public activeTill: string = null;

    public timeLeft = 0;

    public engine: string = null;

    public fuel: string = null;

    public kw: number = null;

    public transmission: string = null;

    public highestBid = 0;

    public price: number = null;

    public startPrice: number = null;

    public vatDeductible = false;

    public damage: string = null;

    public color: string = null;

    public co2Value: number = null;

    @Type(Picture) pictures: Picture[] = [];

    @Type(CarDamage) public damageDescription: CarDamage[] = [];

    @Type(CarOption) public options: CarOption[] = [];

    public numberOfBids: number = null;

    public isSold = true;

    public makeId: string = null;

    public modelId: string = null;

    public fuelTypeId: string = null;

    public transmissionTypeId: string = null;

    public colorId: string = null;

    get firstPicture(): Picture {
        return (this.pictures && this.pictures[0])
            ? this.pictures[0]
            : null;
    }

    get pictureUrl(): string {
        return this.firstPicture
            ? this.firstPicture.url
            : '';
    }

    get thumbnailUrl(): string {
        return this.firstPicture
            ? this.firstPicture.thumb380Url
            : '';
    }

    get mileageString(): string {
        return `${this.formatter.formatNumber(this.mileage, ' ')} km`;
    }

    get powerString(): string {
        return `${this.kw} kw / ${this.hp} hp`;
    }

    get hp(): number {
        return Math.floor(this.kw * 1.35962);
    }

    get highestBidString(): string {
        return `€ ${this.formatter.formatNumber(this.highestBid)},-`;
    }

    get highestBidVat(): number {
        if (this.vatDeductible) {
            return Math.ceil(this.highestBid * (this.variables.vat / 100));
        }

        return 0;
    }

    get highestBidVatString(): string {
        return `€ ${this.formatter.formatNumber(this.highestBidVat)},-`;
    }

    get highestBidTotal(): number {
        return this.highestBid + this.highestBidVat + this.variables.fee;
    }

    get highestBidTotalString(): string {
        return `€ ${this.formatter.formatNumber(this.highestBidTotal)},-`;
    }

    get highestBidNet(): number {
        return Math.ceil(this.highestBid / (1 + this.variables.vat / 100));
    }

    get highestBidNetString(): string {
        return `€ ${this.formatter.formatNumber(this.highestBidNet)},-`;
    }

    get nextLowestBid(): number {
        return this.highestBid + (this.highestBid === 0 ? this.variables.minFirstBid : this.variables.minBidGrow);
    }

    get priceString(): string {
        return `€ ${this.formatter.formatNumber(this.price)},-`;
    }

    get hasDamage(): boolean {
        return !!this.damage;
    }

    public get descriptionHtml(): string {
        return this.description ? this.description.replace(/(?:\r\n|\r|\n)/g, '<br>') : '';
    }

    markSold() {
        this.isSold = true;
    }

    hasOption(option: CarOption): boolean {
        return this.options.some((currentOption) => currentOption.id === option.id);
    }

    toggleOption(option: CarOption) {
        for (let n = 0; n < this.options.length; n += 1) {
            if (this.options[n].id === option.id) {
                return this.options.splice(n, 1);
            }
        }

        return this.options.push(option);
    }

    hasDamageSelected(damage: CarDamage): boolean {
        return this.damageDescription.some((currentDamage) => currentDamage.id === damage.id);
    }

    toggleDamage(damage: CarDamage) {
        for (let n = 0; n < this.damageDescription.length; n += 1) {
            if (this.damageDescription[n].id === damage.id) {
                return this.damageDescription.splice(n, 1);
            }
        }

        return this.damageDescription.push(damage);
    }

    removePicture(picture: Picture) {
        for (let n = 0; n < this.pictures.length; n += 1) {
            if (this.pictures[n].url === picture.url) {
                this.pictures.splice(n, 1);
                break;
            }
        }
    }

    addPicture(picture: Picture) {
        this.pictures.push(picture);
    }

    fromJson(jsonData: any): Car {
        super.fromJson(jsonData);

        if (this.activeTill) {
            this.activeTill = moment.default(this.activeTill).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        return this;
    }
}
